import { Scene } from "onearm";
import { Signal } from "typed-signals";
import { InfoDisplay } from "./hud/InfoDisplay.js";
import { LineWinDisplay } from "./hud/LineWinDisplay.js";

export class HUDScene extends Scene {
    static MODE_STATES = {
        idle:      { SpinButton: true,  StopButton: false, BetButton: true,  AutoPlayButton: true,  MenuButton: true,  InfoButton: true  },
        spinning:  { SpinButton: true,  StopButton: false, BetButton: false, AutoPlayButton: false, MenuButton: false, InfoButton: false },
        autoplay:  { SpinButton: false, StopButton: true,  BetButton: false, AutoPlayButton: false, MenuButton: true,  InfoButton: true  },
        popup:     { SpinButton: false, StopButton: false, BetButton: false, AutoPlayButton: false, MenuButton: false, InfoButton: false },
    };

    // Signals
    onSpin = new Signal();
    onStop = new Signal();
    onAutoplay = new Signal();
    onStopAutoplay = new Signal();
    onSettings = new Signal();
    onInfo = new Signal();
    onBetSelect = new Signal();
    onSoundToggle = new Signal();

    _spinConnection = null;
    _isSpinState = true;
    _isAutoplayActive = false;
    _currentMode = "idle";

    create() {
        super.create();

        this.info = new InfoDisplay(this.layout, { currency: "FUN" });
        this.lineWin = new LineWinDisplay(this.layout, {
            currency: "FUN",
            resources: this.services.get("resources"),
        });

        const spinBtn = this.layout.find("SpinButton");
        if (spinBtn && spinBtn.onPress) {
            this._spinBtn = spinBtn;
            this._spinConnection = spinBtn.onPress.connect(() => {
                if (this._isSpinState) {
                    this.onSpin.emit();
                } else {
                    this.onStop.emit();
                }
            });
        }

        this._connectButton("AutoPlayButton", "onAutoplay");
        this._connectButton("MenuButton", "onSettings");
        this._connectButton("InfoButton", "onInfo");
        this._connectButton("BetButton", "onBetSelect");

        const stopBtn = this.layout.find("StopButton");
        if (stopBtn && stopBtn.onPress) {
            this._stopBtn = stopBtn;
            stopBtn.onPress.connect(() => this.onStopAutoplay.emit());
            stopBtn.visible = false;
        }

        this._autoPlayButton = this.layout.find("AutoPlayButton");
        this._autoplayCounter = this.layout.find("AutoplayCounter");
        this._autoplayValue = this.layout.find("AutoplayCounter.value");

        if (this._autoplayCounter) {
            this._autoplayCounter.visible = false;
        }

        this._soundToggle = this.layout.find("SoundToggle");
        if (this._soundToggle && this._soundToggle.onChange) {
            this._soundToggle.onChange.connect((value) => this.onSoundToggle.emit(value));
        }
    }

    setSoundState(isSoundOn) {
        if (this._soundToggle) this._soundToggle.setState(isSoundOn);
    }

    _connectButton(name, signalName) {
        const btn = this.layout.find(name);
        if (btn && btn.onPress) {
            btn.onPress.connect(() => this[signalName].emit());
        }
    }

    // --- State ---

    setSpinState() {
        this._isSpinState = true;
    }

    setStopState() {
        this._isSpinState = false;
    }

    _applyMode(mode) {
        this._currentMode = mode;

        const state = HUDScene.MODE_STATES[mode];
        for (const name in state) {
            const isEnabled = name === "StopButton" && this._isAutoplayActive
                ? true
                : state[name];

            this.layout.forAll(name, (obj) => { obj.enabled = isEnabled; });
        }
    }

    setIdleState() { this.setSpinState(); this._applyMode("idle"); }
    setSpinningState() { this.setStopState(); this._applyMode("spinning"); }
    setPresentationState() { this.setStopState(); this._applyMode("spinning"); }
    setAutoplayState() { this._applyMode("autoplay"); }
    setPopupState() { this._applyMode("popup"); }

    // --- Values ---

    setBalance(amount) { this.info.setBalance(amount); }

    setBet(amount) { this.info.setBet(amount); }

    setWin(amount) { this.info.setWin(amount); }

    animBalance(amount) { this.info.animBalance(amount); }

    showWin() { this.info.showWin(); }

    hideWin() { this.info.hideWin(); }

    resetWin() { this.info.resetWin(); }

    addWin(amount) { this.info.addWin(amount); }

    setLineWin(value) { this.lineWin.setValue(value); }

    fadeInLineWin(options) { return this.lineWin.fadeIn(options); }

    fadeOutLineWin(options) { return this.lineWin.fadeOut(options); }

    hideLineWin() { this.lineWin.hide(); }

    // --- Autoplay ---

    setAutoplayActive(isActive) {
        this._isAutoplayActive = isActive;

        if (this._autoplayCounter) this._autoplayCounter.visible = isActive;
        if (this._autoPlayButton) this._autoPlayButton.visible = !isActive;
        if (this._stopBtn) {
            this._stopBtn.visible = isActive;
            this._stopBtn.enabled = isActive;
        }
        if (this._spinBtn) this._spinBtn.visible = !isActive;

        if (!isActive) this._applyMode(this._currentMode);
    }

    setAutoplayCount(count) {
        if (this._autoplayValue) {
            this._autoplayValue.text = String(count);
        }
    }
}
